import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactPopup from '../components/ContactPopup';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const DataAnalystCourse = () => {
  const [popupState, setPopupState] = useState({
    isOpen: false,
    title: "Enroll in 10x Data Analyst Program",
    subtitle: "Start your journey to becoming a data analyst",
    formType: "course_enrollment",
    interestedProgram: "10x Data Analyst and AI Complete Certificate Program"
  });

  const openPopup = (config: {
    title?: string;
    subtitle?: string;
    formType?: string;
    interestedProgram?: string;
  } = {}) => {
    setPopupState({
      isOpen: true,
      title: config.title || "Enroll in 10x Data Analyst Program",
      subtitle: config.subtitle || "Start your journey to becoming a data analyst",
      formType: config.formType || "course_enrollment",
      interestedProgram: config.interestedProgram || "10x Data Analyst and AI Complete Certificate Program"
    });
  };

  const closePopup = () => {
    setPopupState(prev => ({ ...prev, isOpen: false }));
  };

  // Course modules data
  const modules = [
    {
      title: "Data Analytics with Google Sheets: Analytics, Automation, and AI",
      lessons: [
        "Getting Started with Google Sheets",
        "Mastering the Basics of Google Sheets",
        "Mastering Google Sheets Functions",
        "Pivot Table Foundations: Mastering KPIs and Data Analysis",
        "Charts and Dashboarding: Advanced Techniques for Data Mastery",
        "Introduction to Google Apps Script",
        "Building Blocks of Google Apps Script",
        "Custom Functions Deep Dive in Google Sheets",
        "From Mockups to AI: Preparing for OpenAI GPT Integration",
        "Bringing GPT to Life: Implementing Real AI Functions in Google Sheets",
        "Implementing Advanced GPT Functions"
      ]
    },
    {
      title: "Data Visualisation and Dashboarding with Tableau",
      lessons: [
        "Introduction to Tableau",
        "Chart Creation Fundamentals",
        "Data Preparation Techniques for Tableau",
        "Enterprise Dashboarding on Tableau",
        "Data Modelling and Cleaning in Tableau",
        "Visualizing Live Datasets in Tableau",
        "Tableau Functions & Calculations",
        "Understanding and Implementing Level of Detail (LOD) Expressions",
        "Data Blending and Publishing in Tableau"
      ]
    },
    {
      title: "Data Warehousing, ETL Pipelines using SQL and BigQuery",
      lessons: [
        "Introduction to Data Warehousing",
        "Data Warehouse Architectures and Design Principles",
        "SQL Commands Fundamentals",
        "Advanced SQL for Data Retrieval",
        "Data Manipulation and Transformation with SQL",
        "Performance Tuning Fundamentals",
        "Introduction to ETL Processes",
        "Building ETL Pipelines with SQL",
        "Introduction to BigQuery for Data Analysis",
        "Advanced BigQuery Techniques",
        "Data Governance in Data Warehousing",
        "Security Best Practices for Data Warehouses"
      ]
    },
    {
      title: "AI Driven Data Analytics with Python",
      lessons: [
        "Introduction to Python for Data Analytics",
        "Python Data Structures and Control Flow",
        "Functions and Modules in Python",
        "Introduction to NumPy for Numerical Computing",
        "Data Cleaning and Preprocessing",
        "Advanced Data Manipulation with Pandas",
        "Data Normalization and Standardization Techniques",
        "Descriptive Statistics in Python",
        "Exploratory Data Analysis (EDA) Fundamentals",
        "Data Transformation Techniques",
        "Feature Engineering Strategies",
        "Advanced Pandas Operation",
        "Data Visualization with Matplotlib",
        "Advanced Plotting with Seaborn",
        "Interactive Visualizations using Plotly and Cufflinks",
        "Introduction to OpenAI's GPT Models",
        "Integrating ChatGPT with Python for Data Analysis",
        "Automated Reporting and Analysis using Python and AI"
      ]
    },
    {
      title: "Statistical Modeling and Machine Learning for Data Analytics",
      lessons: [
        "Introduction to Statistical Modeling",
        "Probability Theory and Distributions",
        "Overview of Machine Learning Concepts",
        "Correlation Analysis and Its Applications",
        "Simple Linear Regression",
        "Multiple Linear Regression and Polynomial Regression",
        "Classification Algorithms (Logistic Regression, Decision Trees)",
        "Clustering Techniques (K-means, Hierarchical Clustering)",
        "Introduction to Time Series Analysis and Forecasting",
        "Ensemble Methods (Decision Tree, Random Forests, Gradient Boosting)",
        "Hyperparameter Tuning and Cross-Validation",
        "Dimensionality Reduction Techniques (PCA, t-SNE)"
      ]
    }
  ];

  // Updated Projects data with trending latest projects
  const projects = [
    {
      title: "Netflix Content Analytics",
      subtitle: "AI-Powered Streaming Intelligence Dashboard",
      description: "Build advanced dashboards analyzing Netflix viewing patterns, content performance, and user engagement using machine learning algorithms and predictive analytics.",
      value: "$599",
      skills: ["Machine Learning", "Streaming Analytics", "User Behavior Analysis", "Predictive Modeling"],
      difficulty: "Advanced",
      iconSvg: (
        <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
          <path d="M4 3h16c1.1 0 2 .9 2 2v14c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V5c0-1.1.9-2 2-2zm8 5v8l6-4-6-4z"/>
        </svg>
      )
    },
    {
      title: "Cryptocurrency Market Tracker",
      subtitle: "Real-Time Crypto Trading Dashboard",
      description: "Create dynamic crypto trading dashboards with real-time price feeds, sentiment analysis, and automated trading signals using Python and API integrations.",
      value: "$699",
      skills: ["API Integration", "Real-Time Analytics", "Financial Modeling", "Sentiment Analysis"],
      difficulty: "Expert",
      iconSvg: (
        <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm1-13h-2v4l2.8 1.4.7-1.3L13 11V7z"/>
        </svg>
      )
    },
    {
      title: "Social Media Sentiment Engine",
      subtitle: "AI-Driven Brand Perception Analysis",
      description: "Develop comprehensive social media monitoring systems using NLP and sentiment analysis to track brand perception across multiple platforms.",
      value: "$549",
      skills: ["Natural Language Processing", "Social Media Analytics", "Brand Intelligence", "Data Mining"],
      difficulty: "Advanced",
      iconSvg: (
        <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
          <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z"/>
        </svg>
      )
    },
    {
      title: "E-Commerce Growth Optimizer",
      subtitle: "Customer Journey & Conversion Analytics",
      description: "Build end-to-end e-commerce analytics platform tracking customer journey, optimizing conversion funnels, and predicting customer lifetime value.",
      value: "$649",
      skills: ["Customer Analytics", "Conversion Optimization", "Cohort Analysis", "Revenue Forecasting"],
      difficulty: "Advanced",
      iconSvg: (
        <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
          <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
        </svg>
      )
    },
    {
      title: "Climate Change Data Explorer",
      subtitle: "Environmental Impact Visualization",
      description: "Create interactive dashboards visualizing climate data, carbon footprint tracking, and environmental impact analysis for sustainability reporting.",
      value: "$499",
      skills: ["Environmental Analytics", "Geospatial Analysis", "Sustainability Metrics", "Climate Modeling"],
      difficulty: "Intermediate",
      iconSvg: (
        <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.9 17.39c-.26-.8-1.01-1.39-1.9-1.39h-1v-3a.5.5 0 0 0-.5-.5h-6v-2h2a.5.5 0 0 0 .5-.5v-2h2a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 0-.5.5v6.5a.5.5 0 0 0 .5.5h6v3c0 .55.45 1 1 1h1a1 1 0 0 1 1 1 1 1 0 0 1-1 1c-.83 0-1.54-.5-1.85-1.22l-.05-.13-.01-.02A2 2 0 0 1 14.98 16H13a2 2 0 0 1-2-2v-4H9.5A3.5 3.5 0 0 0 6 13.5V16a6 6 0 0 0 6 6c2.97 0 5.43-2.16 5.9-5h.1a3 3 0 0 0 0-6z"/>
        </svg>
      )
    },
    {
      title: "Healthcare AI Diagnostics",
      subtitle: "Medical Data Analysis Platform",
      description: "Develop AI-powered healthcare analytics for patient outcome prediction, disease pattern recognition, and medical resource optimization.",
      value: "$799",
      skills: ["Healthcare Analytics", "Medical AI", "Predictive Healthcare", "Patient Outcome Analysis"],
      difficulty: "Expert",
      iconSvg: (
        <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm8 7h-2v4.5c0 .83-.67 1.5-1.5 1.5s-1.5-.67-1.5-1.5V10c0-2.21-1.79-4-4-4s-4 1.79-4 4v3.5c0 .83-.67 1.5-1.5 1.5S4 14.33 4 13.5V9H2v4.5C2 15.43 3.57 17 5.5 17s3.5-1.57 3.5-3.5V10c0-1.1.9-2 2-2s2 .9 2 2v3.5c0 1.93 1.57 3.5 3.5 3.5s3.5-1.57 3.5-3.5V9z"/>
        </svg>
      )
    },
    {
      title: "Smart City Traffic Flow",
      subtitle: "Urban Mobility Intelligence System",
      description: "Build intelligent traffic management systems using IoT data, real-time analytics, and predictive modeling for smart city applications.",
      value: "$599",
      skills: ["IoT Analytics", "Urban Planning", "Traffic Optimization", "Smart City Solutions"],
      difficulty: "Advanced",
      iconSvg: (
        <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
        </svg>
      )
    },
    {
      title: "Supply Chain Optimizer",
      subtitle: "AI-Powered Logistics Dashboard",
      description: "Create comprehensive supply chain analytics platform with demand forecasting, inventory optimization, and risk assessment capabilities.",
      value: "$649",
      skills: ["Supply Chain Analytics", "Demand Forecasting", "Inventory Management", "Risk Analysis"],
      difficulty: "Advanced",
      iconSvg: (
        <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      )
    }
  ];



  // Tools covered
  const tools = [
    { name: "Google Sheets", icon: "📊" },
    { name: "Tableau", icon: "📈" },
    { name: "SQL", icon: "🗄️" },
    { name: "BigQuery", icon: "☁️" },
    { name: "Python", icon: "🐍" },
    { name: "Pandas", icon: "🐼" },
    { name: "NumPy", icon: "🔢" },
    { name: "Matplotlib", icon: "📉" },
    { name: "Seaborn", icon: "🎨" },
    { name: "Plotly", icon: "📊" },
    { name: "OpenAI GPT", icon: "🤖" },
    { name: "Machine Learning", icon: "🧠" }
  ];



  return (
    <div className="min-h-screen bg-white">
      <Header openPopup={openPopup} />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white min-h-screen flex items-center justify-center overflow-hidden">
        {/* Data Analytics Background Elements */}
        <div className="absolute inset-0">
          {/* Dark background */}
          <div className="absolute inset-0 bg-gray-900"></div>
          
          {/* Data visualization elements */}
          <div className="absolute inset-0 overflow-hidden opacity-20">
            {/* Bar Charts */}
            <div className="absolute top-20 left-16">
              <div className="flex items-end space-x-1">
                <div className="w-2 h-8 bg-blue-400 animate-pulse"></div>
                <div className="w-2 h-12 bg-blue-500 animate-pulse delay-100"></div>
                <div className="w-2 h-6 bg-blue-400 animate-pulse delay-200"></div>
                <div className="w-2 h-16 bg-blue-600 animate-pulse delay-300"></div>
                <div className="w-2 h-10 bg-blue-500 animate-pulse delay-400"></div>
              </div>
            </div>

            {/* Line Chart */}
            <div className="absolute top-32 right-20">
              <svg width="80" height="40" className="stroke-purple-400 animate-pulse">
                <polyline
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  points="0,35 15,25 30,30 45,15 60,20 75,10"
                />
                <circle cx="0" cy="35" r="2" fill="currentColor" />
                <circle cx="15" cy="25" r="2" fill="currentColor" />
                <circle cx="30" cy="30" r="2" fill="currentColor" />
                <circle cx="45" cy="15" r="2" fill="currentColor" />
                <circle cx="60" cy="20" r="2" fill="currentColor" />
                <circle cx="75" cy="10" r="2" fill="currentColor" />
              </svg>
            </div>

            {/* Pie Chart */}
            <div className="absolute bottom-32 left-24">
              <svg width="60" height="60" className="animate-spin" style={{ animationDuration: '10s' }}>
                <circle cx="30" cy="30" r="25" fill="none" stroke="#3B82F6" strokeWidth="6" strokeDasharray="40 120" />
                <circle cx="30" cy="30" r="25" fill="none" stroke="#8B5CF6" strokeWidth="6" strokeDasharray="30 120" strokeDashoffset="-40" />
                <circle cx="30" cy="30" r="25" fill="none" stroke="#06B6D4" strokeWidth="6" strokeDasharray="50 120" strokeDashoffset="-70" />
              </svg>
            </div>

            {/* Data Tables */}
            <div className="absolute top-1/2 left-12">
              <div className="space-y-1">
                <div className="flex space-x-1">
                  <div className="w-8 h-1 bg-blue-400 animate-pulse"></div>
                  <div className="w-6 h-1 bg-blue-300 animate-pulse delay-100"></div>
                  <div className="w-10 h-1 bg-blue-500 animate-pulse delay-200"></div>
                </div>
                <div className="flex space-x-1">
                  <div className="w-6 h-1 bg-purple-400 animate-pulse delay-100"></div>
                  <div className="w-8 h-1 bg-purple-300 animate-pulse delay-200"></div>
                  <div className="w-7 h-1 bg-purple-500 animate-pulse delay-300"></div>
                </div>
                <div className="flex space-x-1">
                  <div className="w-10 h-1 bg-cyan-400 animate-pulse delay-200"></div>
                  <div className="w-5 h-1 bg-cyan-300 animate-pulse delay-300"></div>
                  <div className="w-9 h-1 bg-cyan-500 animate-pulse delay-400"></div>
                </div>
              </div>
            </div>

            {/* Dashboard Cards */}
            <div className="absolute bottom-20 right-16">
              <div className="space-y-2">
                <div className="w-16 h-8 border border-blue-400/50 rounded animate-pulse">
                  <div className="p-1">
                    <div className="w-8 h-1 bg-blue-400 mb-1"></div>
                    <div className="w-12 h-1 bg-blue-300"></div>
                  </div>
                </div>
                <div className="w-16 h-8 border border-purple-400/50 rounded animate-pulse delay-100">
                  <div className="p-1">
                    <div className="w-10 h-1 bg-purple-400 mb-1"></div>
                    <div className="w-8 h-1 bg-purple-300"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Analytics Icons */}
            <div className="absolute top-3/4 right-1/3">
              <svg width="40" height="40" className="text-blue-400 animate-bounce" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4zm2.5 2.5h-15V5h15v14.5zm0-16.5h-15c-.83 0-1.5.67-1.5 1.5v15c0 .83.67 1.5 1.5 1.5h15c.83 0 1.5-.67 1.5-1.5v-15c0-.83-.67-1.5-1.5-1.5z"/>
              </svg>
            </div>

            {/* Trending Arrow */}
            <div className="absolute top-1/4 right-1/4">
              <svg width="32" height="32" className="text-green-400 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6h-6z"/>
              </svg>
            </div>
          </div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 w-full max-w-5xl mx-auto px-6 lg:px-12 pt-2 pb-8">
          <div className="text-center">
            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight tracking-tight">
              <span className="inline-block animate-pulse">10X</span>{" "}
              <span className="text-white">Your Data Analytics Career with</span>{" "}
              <span className="bg-gradient-to-r from-green-400 via-blue-400 to-purple-400 bg-clip-text text-transparent font-extrabold">
                AI-Powered Skills
              </span>
            </h1>
            
            {/* Subtitle */}
            <div className="mb-8 max-w-3xl mx-auto">
              <p className="text-lg sm:text-xl text-gray-300 font-light leading-relaxed">
                <span className="text-yellow-400 font-semibold">Go from beginner to confident practitioner</span> in 6 months with hands-on training.{" "}
                <span className="text-blue-400 font-semibold">Build practical data skills with guided projects.</span>
              </p>
            </div>

            {/* Key Stats - Redesigned */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 max-w-3xl mx-auto">
              <div className="group relative overflow-hidden bg-gradient-to-br from-blue-500/20 to-blue-600/30 backdrop-blur-lg rounded-2xl p-5 border border-blue-400/30 hover:border-blue-400/60 transition-all duration-300 hover:scale-105">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative text-center">
                  <div className="mb-3">
                    <svg className="w-8 h-8 text-blue-300 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6L23 9l-11-6zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
                    </svg>
                  </div>
                  <div className="text-xs font-semibold text-blue-200 mb-1 uppercase tracking-wider">1to10x Certificate</div>
                  <div className="text-white font-bold text-sm">Upon Completion</div>
                </div>
              </div>
              
              <div className="group relative overflow-hidden bg-gradient-to-br from-purple-500/20 to-purple-600/30 backdrop-blur-lg rounded-2xl p-5 border border-purple-400/30 hover:border-purple-400/60 transition-all duration-300 hover:scale-105">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative text-center">
                  <div className="mb-3">
                    <svg className="w-8 h-8 text-purple-300 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8zm.5-13H11v6l5.2 3.2.8-1.3-4.5-2.7V7z"/>
                    </svg>
                  </div>
                  <div className="text-xs font-semibold text-purple-200 mb-1 uppercase tracking-wider">6 Months</div>
                  <div className="text-white font-bold text-sm">Intensive Training</div>
                </div>
              </div>
              
              <div className="group relative overflow-hidden bg-gradient-to-br from-green-500/20 to-green-600/30 backdrop-blur-lg rounded-2xl p-5 border border-green-400/30 hover:border-green-400/60 transition-all duration-300 hover:scale-105">
                <div className="absolute inset-0 bg-gradient-to-br from-green-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative text-center">
                  <div className="mb-3">
                    <svg className="w-8 h-8 text-green-300 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M2.5 19h19v2h-19v-2zm1.9-5L12 8.5l7.6 5.5 1.2-1.7L12 4 3.2 12.3l1.2 1.7zm8.6-7.5c0-.8.7-1.5 1.5-1.5s1.5.7 1.5 1.5-.7 1.5-1.5 1.5-1.5-.7-1.5-1.5z"/>
                    </svg>
                  </div>
                  <div className="text-xs font-semibold text-green-200 mb-1 uppercase tracking-wider">25+ Projects</div>
                  <div className="text-white font-bold text-sm">Real-World Experience</div>
                </div>
              </div>
            </div>

            {/* Call-to-Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center w-full px-4 mt-6">
              <a 
                href="https://academy.10xdataanalyst.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-medium transition-all duration-200 hover:scale-105 text-sm text-center whitespace-nowrap inline-block"
                style={{ minWidth: 'max-content' }}
              >
                Enrol Now
              </a>
              <button 
                onClick={() => openPopup({
                  title: "Book FREE Strategy Call",
                  subtitle: "Connect with our data analytics experts to discuss your career goals",
                  formType: "strategy_call"
                })}
                className="border border-white/50 text-white hover:bg-white hover:text-gray-900 px-6 py-3 rounded-full font-medium transition-all duration-200 hover:scale-105 text-sm text-center whitespace-nowrap inline-block"
                style={{ minWidth: 'max-content' }}
              >
                Book FREE Strategy Call
              </button>
            </div>

            {/* Rating */}
            <div className="mt-8 text-center">
              <p className="text-gray-300">
                This course is rated as <span className="text-yellow-400 font-bold">excellent</span> by 90% of the learners
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* Course Benefits */}
      <section className="py-20 px-4" style={{ backgroundColor: '#F1EFED' }}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Here's how this program will <span className="text-blue-600">10x YOUR CAREER</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Six key advantages that will transform your data analytics career
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-16">
            {[
              {
                icon: (
                  <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                ),
                title: "1to10x Certificate",
                description: "Earn a 1to10x certificate that reflects your completed coursework, applied projects, and practical analytics skills."
              },
              {
                icon: (
                  <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ),
                title: "AI-Powered Analytics Mastery",
                description: "Master cutting-edge generative AI tools and techniques for advanced data analysis, putting you at the forefront of the data science revolution with next-generation skills."
              },
              {
                icon: (
                  <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                ),
                title: "Real-World Project Portfolio",
                description: "Build an impressive portfolio with 25+ industry-standard projects that demonstrate your practical skills and showcase your analytical capabilities."
              },
              {
                icon: (
                  <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                ),
                title: "Professional Growth Network",
                description: "Connect with industry professionals, join guided discussions, and receive structured support for continued career growth."
              },
              {
                icon: (
                  <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                ),
                title: "Future-Ready Technology Stack",
                description: "Stay ahead of industry trends with comprehensive training in the latest data analytics tools, AI technologies, and emerging platforms that define the future of data science."
              },
              {
                icon: (
                  <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9 3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                ),
                title: "Global Professional Community",
                description: "Join an exclusive network of ambitious data professionals across 50+ countries, with lifetime access to peer connections, mentorship opportunities, and collaborative projects."
              }
            ].map((benefit, idx) => (
              <div key={idx} className="text-left">
                {/* Icon */}
                <div className="mb-6">
                  {benefit.icon}
                </div>
                
                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {benefit.title}
                </h3>
                
                {/* Description */}
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Curriculum */}
      <section className="relative">
        {/* Full Width Separator */}
        <div className="w-full h-px bg-gray-300 absolute top-0 left-0"></div>
        
        <div className="py-20 px-4" style={{ backgroundColor: '#F1EFED' }}>
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                What You'll <span className="text-blue-600">Master</span> and Apply in This Program
              </h2>
              <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                Our classes are 80% hands-on and 20% theory, so you'll start working on your portfolio from day one!
              </p>
            </div>

            {/* Curriculum Accordion with SVG Icons */}
            <div className="max-w-4xl mx-auto">
              <Accordion type="single" collapsible className="space-y-4">
                {modules.map((module, idx) => {
                  // SVG icons for each module
                  const moduleIcons = [
                    // Module 1: Google Sheets - Spreadsheet icon
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M3 3h18v18H3V3zm16 16V5H5v14h14zM7 7h2v2H7V7zm4 0h2v2h-2V7zm4 0h2v2h-2V7zM7 11h2v2H7v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2zM7 15h2v2H7v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2z"/>
                    </svg>,
                    // Module 2: Tableau - Charts/Dashboard icon
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M22 7.5h-1.5V6c0-1.1-.9-2-2-2s-2 .9-2 2v1.5H15c-1.1 0-2 .9-2 2s.9 2 2 2h1.5V13c0 1.1.9 2 2 2s2-.9 2-2v-1.5H22c1.1 0 2-.9 2-2s-.9-2-2-2zM9 7.5H7.5V6c0-1.1-.9-2-2-2S3.5 4.9 3.5 6v1.5H2c-1.1 0-2 .9-2 2s.9 2 2 2h1.5V13c0 1.1.9 2 2 2s2-.9 2-2v-1.5H9c1.1 0 2-.9 2-2s-.9-2-2-2zm6.5 8H14v-1.5c0-1.1-.9-2-2-2s-2 .9-2 2v1.5H8.5c-1.1 0-2 .9-2 2s.9 2 2 2H10V22c0 1.1.9 2 2 2s2-.9 2-2v-1.5h1.5c1.1 0 2-.9 2-2s-.9-2-2-2z"/>
                    </svg>,
                    // Module 3: SQL/Database - Database icon
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 3C7.58 3 4 4.79 4 7s3.58 4 8 4 8-1.79 8-4-3.58-4-8-4zM4 9v3c0 2.21 3.58 4 8 4s8-1.79 8-4V9c0 2.21-3.58 4-8 4s-8-1.79-8-4zm0 5v3c0 2.21 3.58 4 8 4s8-1.79 8-4v-3c0 2.21-3.58 4-8 4s-8-1.79-8-4z"/>
                    </svg>,
                    // Module 4: Python - Code/Programming icon
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0L19.2 12l-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
                    </svg>,
                    // Module 5: Machine Learning - Brain/AI icon
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12c0 5.52 4.48 10 10 10s10-4.48 10-10c0-5.52-4.48-10-10-10zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      <path d="M7.5 14c.83 0 1.5-.67 1.5-1.5S8.33 11 7.5 11 6 11.67 6 12.5 6.67 14 7.5 14zm4.5-4c.83 0 1.5-.67 1.5-1.5S12.83 7 12 7s-1.5.67-1.5 1.5S11.17 10 12 10zm4.5 4c.83 0 1.5-.67 1.5-1.5S17.33 11 16.5 11 15 11.67 15 12.5 15.67 14 16.5 14z"/>
                    </svg>
                  ];

                  return (
                    <AccordionItem
                      key={idx}
                      value={`module-${idx}`}
                      className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-all duration-300"
                    >
                      <AccordionTrigger className="px-8 py-6 text-left hover:no-underline hover:bg-gray-50 transition-colors">
                        <div className="flex items-center space-x-4 w-full">
                          <div className="flex-shrink-0 w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                            {moduleIcons[idx]}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 text-left">
                              {module.title}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1 text-left">
                              {module.lessons.length} lessons
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-8 pb-8 pt-4">
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {module.lessons.map((lesson, lessonIdx) => (
                              <div 
                                key={lessonIdx} 
                                className="flex items-start space-x-3 p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:scale-[1.02] border border-gray-100"
                              >
                                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg flex items-center justify-center font-bold text-sm shadow-sm">
                                  {lessonIdx + 1}
                                </div>
                                <div className="flex-1">
                                  <span className="text-gray-800 leading-relaxed font-medium text-sm">
                                    {lesson}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Module Summary */}
                          <div className="mt-6 pt-4 border-t border-blue-200">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-blue-700 font-semibold">
                                📚 Total Lessons: {module.lessons.length}
                              </span>
                              <span className="text-blue-600 font-medium">
                                🎯 Hands-on Learning
                              </span>
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* Real-World Projects */}
      <section className="py-20 px-4" style={{ backgroundColor: '#111827' }}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Build Your <span className="text-blue-400">Portfolio</span> with <span className="text-blue-400">Trending Projects</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              <span className="text-blue-400 font-semibold">25+ cutting-edge projects</span> that mirror real industry challenges across analytics, operations, and product workflows.
              <span className="text-purple-400 font-semibold"> Worth $4,800+ in value!</span>
            </p>
          </div>

          {/* Scrolling Projects Container */}
          <div className="relative overflow-hidden">
            <div className="animate-scroll flex gap-8 w-max">
              {/* First set of projects */}
              {projects.map((project, idx) => (
                <div
                  key={idx}
                  className="flex-shrink-0 w-80 group"
                >
                  <div className="relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-gray-100 h-[350px] flex flex-col overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-50 to-purple-50 rounded-full -translate-y-16 translate-x-16 opacity-60"></div>
                    
                    {/* Header */}
                    <div className="relative z-10 flex items-start justify-between mb-6">
                      <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 group-hover:scale-110 transition-transform duration-300 flex items-center justify-center">
                        <div className="w-6 h-6 flex items-center justify-center">
                          {project.iconSvg}
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        project.difficulty === 'Expert' ? 'bg-red-500 text-white' :
                        project.difficulty === 'Advanced' ? 'bg-orange-500 text-white' :
                        'bg-green-500 text-white'
                      } shadow-md`}>
                        {project.difficulty}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="relative z-10 flex-grow flex flex-col">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors leading-tight">
                        {project.title}
                      </h3>
                      <p className="text-sm font-medium text-blue-600 mb-4">
                        {project.subtitle}
                      </p>

                      <p className="text-gray-600 mb-6 leading-relaxed text-sm flex-grow">
                        {project.description}
                      </p>
                      
                      {/* Skills */}
                      <div className="mb-8">
                        <div className="flex flex-wrap gap-2">
                          {project.skills.slice(0, 2).map((skill, skillIdx) => (
                            <span key={skillIdx} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                              {skill}
                            </span>
                          ))}
                          {project.skills.length > 2 && (
                            <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-medium">
                              +{project.skills.length - 2} more
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Duplicate set for seamless scrolling */}
              {projects.map((project, idx) => (
                <div
                  key={`duplicate-${idx}`}
                  className="flex-shrink-0 w-80 group"
                >
                  <div className="relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-gray-100 h-[350px] flex flex-col overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-50 to-purple-50 rounded-full -translate-y-16 translate-x-16 opacity-60"></div>
                    
                    {/* Header */}
                    <div className="relative z-10 flex items-start justify-between mb-6">
                      <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 group-hover:scale-110 transition-transform duration-300 flex items-center justify-center">
                        <div className="w-6 h-6 flex items-center justify-center">
                          {project.iconSvg}
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        project.difficulty === 'Expert' ? 'bg-red-500 text-white' :
                        project.difficulty === 'Advanced' ? 'bg-orange-500 text-white' :
                        'bg-green-500 text-white'
                      } shadow-md`}>
                        {project.difficulty}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="relative z-10 flex-grow flex flex-col">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors leading-tight">
                        {project.title}
                      </h3>
                      <p className="text-sm font-medium text-blue-600 mb-4">
                        {project.subtitle}
                      </p>

                      <p className="text-gray-600 mb-6 leading-relaxed text-sm flex-grow">
                        {project.description}
                      </p>
                      
                      {/* Skills */}
                      <div className="mb-8">
                        <div className="flex flex-wrap gap-2">
                          {project.skills.slice(0, 2).map((skill, skillIdx) => (
                            <span key={skillIdx} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                              {skill}
                            </span>
                          ))}
                          {project.skills.length > 2 && (
                            <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-medium">
                              +{project.skills.length - 2} more
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>


        </div>

        {/* Custom CSS for scrolling animation */}
        <style dangerouslySetInnerHTML={{
          __html: `
            .animate-scroll {
              animation: scroll 200s linear infinite;
            }
            
            .animate-scroll:hover {
              animation-play-state: paused;
            }
            
            .animate-scroll-mentors {
              animation: scroll-mentors 120s linear infinite;
            }
            
            .animate-scroll-mentors:hover {
              animation-play-state: paused;
            }
            
            @keyframes scroll {
              0% {
                transform: translateX(0);
              }
              100% {
                transform: translateX(-50%);
              }
            }
            
            @keyframes scroll-mentors {
              0% {
                transform: translateX(0);
              }
              100% {
                transform: translateX(-50%);
              }
            }
            
            .line-clamp-4 {
              display: -webkit-box;
              -webkit-line-clamp: 4;
              -webkit-box-orient: vertical;
              overflow: hidden;
            }
          `
        }} />
      </section>

      {/* Tools & Technologies */}
      <section className="py-20 px-4" style={{ backgroundColor: '#F1EFED' }}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Master the <span className="text-blue-600">Modern Data Stack</span> with Hands-On Practice
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              <span className="text-blue-600 font-semibold">15+ cutting-edge tools</span> used across modern analytics teams. 
              <span className="text-gray-800 font-medium"> Build practical expertise through guided projects.</span>
            </p>
          </div>

          {/* Scrolling Tools Container */}
          <div className="relative overflow-hidden">
            <div className="animate-scroll flex gap-8 w-max">
              {/* First set of tools */}
              {[
                {
                  name: "Google Sheets",
                  logo: "https://upload.wikimedia.org/wikipedia/commons/3/30/Google_Sheets_logo_%282014-2020%29.svg"
                },
                {
                  name: "Tableau",
                  logo: "https://logos-world.net/wp-content/uploads/2021/10/Tableau-Symbol.png"
                },
                {
                  name: "SQL",
                  logo: "https://cdn-icons-png.flaticon.com/512/4492/4492311.png"
                },
                {
                  name: "BigQuery",
                  logo: "https://cdn.worldvectorlogo.com/logos/google-bigquery-logo-1.svg"
                },
                {
                  name: "Python",
                  logo: "https://upload.wikimedia.org/wikipedia/commons/c/c3/Python-logo-notext.svg"
                },
                {
                  name: "Pandas",
                  logo: "https://upload.wikimedia.org/wikipedia/commons/e/ed/Pandas_logo.svg"
                },
                {
                  name: "NumPy",
                  logo: "https://upload.wikimedia.org/wikipedia/commons/3/31/NumPy_logo_2020.svg"
                },
                {
                  name: "Matplotlib",
                  logo: "https://matplotlib.org/stable/_images/sphx_glr_logos2_003.png"
                },
                {
                  name: "Seaborn",
                  logo: "https://seaborn.pydata.org/_images/logo-mark-lightbg.svg"
                },
                {
                  name: "Plotly",
                  logo: "https://images.plot.ly/logo/new-branding/plotly-logomark.png"
                },
                {
                  name: "OpenAI GPT",
                  logo: "https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg"
                },
                {
                  name: "Scikit-learn",
                  logo: "https://upload.wikimedia.org/wikipedia/commons/0/05/Scikit_learn_logo_small.svg"
                },
                {
                  name: "TensorFlow",
                  logo: "https://upload.wikimedia.org/wikipedia/commons/2/2d/Tensorflow_logo.svg"
                },
                {
                  name: "Jupyter",
                  logo: "https://upload.wikimedia.org/wikipedia/commons/3/38/Jupyter_logo.svg"
                },
                {
                  name: "Power BI",
                  logo: "https://upload.wikimedia.org/wikipedia/commons/c/cf/New_Power_BI_Logo.svg"
                }
              ].map((tool, idx) => (
                <div
                  key={idx}
                  className="flex-shrink-0 bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-gray-100 w-32 h-32 flex flex-col items-center justify-center group"
                >
                  <div className="w-10 h-10 mb-2 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <img
                      src={tool.logo}
                      alt={tool.name}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        // Fallback to colored background with initials if image fails
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = `<div class="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">${tool.name.charAt(0)}</div>`;
                        }
                      }}
                    />
                  </div>
                  <h3 className="font-bold text-gray-900 text-center text-sm leading-tight group-hover:text-blue-600 transition-colors">
                    {tool.name}
                  </h3>
                </div>
              ))}
              
              {/* Duplicate set for seamless scrolling */}
              {[
                {
                  name: "Google Sheets",
                  logo: "https://upload.wikimedia.org/wikipedia/commons/3/30/Google_Sheets_logo_%282014-2020%29.svg"
                },
                {
                  name: "Tableau",
                  logo: "https://logos-world.net/wp-content/uploads/2021/10/Tableau-Symbol.png"
                },
                {
                  name: "SQL",
                  logo: "https://cdn-icons-png.flaticon.com/512/4492/4492311.png"
                },
                {
                  name: "BigQuery",
                  logo: "https://cdn.worldvectorlogo.com/logos/google-bigquery-logo-1.svg"
                },
                {
                  name: "Python",
                  logo: "https://upload.wikimedia.org/wikipedia/commons/c/c3/Python-logo-notext.svg"
                },
                {
                  name: "Pandas",
                  logo: "https://upload.wikimedia.org/wikipedia/commons/e/ed/Pandas_logo.svg"
                },
                {
                  name: "NumPy",
                  logo: "https://upload.wikimedia.org/wikipedia/commons/3/31/NumPy_logo_2020.svg"
                },
                {
                  name: "Matplotlib",
                  logo: "https://matplotlib.org/stable/_images/sphx_glr_logos2_003.png"
                },
                {
                  name: "Seaborn",
                  logo: "https://seaborn.pydata.org/_images/logo-mark-lightbg.svg"
                },
                {
                  name: "Plotly",
                  logo: "https://images.plot.ly/logo/new-branding/plotly-logomark.png"
                },
                {
                  name: "OpenAI GPT",
                  logo: "https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg"
                },
                {
                  name: "Scikit-learn",
                  logo: "https://upload.wikimedia.org/wikipedia/commons/0/05/Scikit_learn_logo_small.svg"
                },
                {
                  name: "TensorFlow",
                  logo: "https://upload.wikimedia.org/wikipedia/commons/2/2d/Tensorflow_logo.svg"
                },
                {
                  name: "Jupyter",
                  logo: "https://upload.wikimedia.org/wikipedia/commons/3/38/Jupyter_logo.svg"
                },
                {
                  name: "Power BI",
                  logo: "https://upload.wikimedia.org/wikipedia/commons/c/cf/New_Power_BI_Logo.svg"
                }
              ].map((tool, idx) => (
                <div
                  key={`duplicate-${idx}`}
                  className="flex-shrink-0 bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-gray-100 w-32 h-32 flex flex-col items-center justify-center group"
                >
                  <div className="w-10 h-10 mb-2 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <img
                      src={tool.logo}
                      alt={tool.name}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        // Fallback to colored background with initials if image fails
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = `<div class="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">${tool.name.charAt(0)}</div>`;
                        }
                      }}
                    />
                  </div>
                  <h3 className="font-bold text-gray-900 text-center text-sm leading-tight group-hover:text-blue-600 transition-colors">
                    {tool.name}
                  </h3>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Custom CSS for scrolling animation */}
        <style dangerouslySetInnerHTML={{
          __html: `
            .animate-scroll {
              animation: scroll 30s linear infinite;
            }
            
            .animate-scroll:hover {
              animation-play-state: paused;
            }
            
            @keyframes scroll {
              0% {
                transform: translateX(0);
              }
              100% {
                transform: translateX(-50%);
              }
            }
          `
                }} />
      </section>

      {/* Separator Line */}
      <div className="w-full h-px bg-gray-300"></div>




      {/* Separator Line */}
      <div className="w-full h-px bg-gray-300"></div>

      {/* Program Overview - Redesigned */}
      <section className="py-20 px-4" style={{ backgroundColor: '#F1EFED' }}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Your Complete <span className="text-blue-600">Transformation</span> Package
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              From zero to industry-ready data professional in 6 months - here's exactly what you get
            </p>
          </div>

          {/* Enhanced Program Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Program Certificate */}
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 relative overflow-hidden group">
              {/* Background Pattern */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-50 to-blue-100 rounded-full -translate-y-16 translate-x-16 opacity-60 group-hover:opacity-80 transition-opacity"></div>
              
              {/* Icon */}
              <div className="relative z-10 w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z"/>
                  <path d="M10 17l-4-4 1.41-1.41L10 14.17l6.59-6.58L18 9l-8 8z"/>
                </svg>
              </div>
              
              {/* Content */}
              <div className="relative z-10">
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                  1to10x Program Certificate
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Earn a 1to10x certificate that reflects your completed coursework and applied analytics projects
                </p>
                <div className="flex items-center text-xs text-blue-600 font-medium">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                  Industry Recognized
                </div>
              </div>
            </div>

            {/* Real-World Projects */}
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 relative overflow-hidden group">
              {/* Background Pattern */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-50 to-purple-100 rounded-full -translate-y-16 translate-x-16 opacity-60 group-hover:opacity-80 transition-opacity"></div>
              
              {/* Icon */}
              <div className="relative z-10 w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M4 3h16c1.1 0 2 .9 2 2v14c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V5c0-1.1.9-2 2-2zm8 5v8l6-4-6-4z"/>
                </svg>
              </div>
              
              {/* Content */}
              <div className="relative z-10">
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-purple-600 transition-colors">
                  25+ Enterprise Projects
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Build Netflix analytics, crypto trackers, AI diagnostics - projects worth $4,800+ that showcase real impact
                </p>
                <div className="flex items-center text-xs text-purple-600 font-medium">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mr-2"></span>
                  Portfolio Ready
                </div>
              </div>
            </div>

            {/* AI-Powered Learning */}
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 relative overflow-hidden group">
              {/* Background Pattern */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-50 to-green-100 rounded-full -translate-y-16 translate-x-16 opacity-60 group-hover:opacity-80 transition-opacity"></div>
              
              {/* Icon */}
              <div className="relative z-10 w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              
              {/* Content */}
              <div className="relative z-10">
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors">
                  AI-Powered Curriculum
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Master ChatGPT integration, machine learning, and next-gen analytics tools before they become mainstream
                </p>
                <div className="flex items-center text-xs text-green-600 font-medium">
                  <span className="w-2 h-2 bg-green-600 rounded-full mr-2"></span>
                  Future Ready
                </div>
              </div>
            </div>

            {/* Expert Mentorship */}
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 relative overflow-hidden group">
              {/* Background Pattern */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-50 to-orange-100 rounded-full -translate-y-16 translate-x-16 opacity-60 group-hover:opacity-80 transition-opacity"></div>
              
              {/* Icon */}
              <div className="relative z-10 w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 17v2H2v-2s0-4 7-4 7 4 7 4z"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="m22 11-3-3v2h-3v2h3v2l3-3z"/>
                </svg>
              </div>
              
              {/* Content */}
              <div className="relative z-10">
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">
                  1:1 Expert Mentorship
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Direct access to experienced mentors who guide your learning and project decisions
                </p>
                <div className="flex items-center text-xs text-orange-600 font-medium">
                  <span className="w-2 h-2 bg-orange-600 rounded-full mr-2"></span>
                  Personal Guidance
                </div>
              </div>
            </div>

            {/* Portfolio Support */}
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 relative overflow-hidden group">
              {/* Background Pattern */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-full -translate-y-16 translate-x-16 opacity-60 group-hover:opacity-80 transition-opacity"></div>
              
              {/* Icon */}
              <div className="relative z-10 w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              
              {/* Content */}
              <div className="relative z-10">
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors">
                  Portfolio Support
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Practical support for resume refinement, interview preparation, and project presentation
                </p>
                <div className="flex items-center text-xs text-indigo-600 font-medium">
                  <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2"></span>
                  Guided Support
                </div>
              </div>
            </div>

            {/* Lifetime Access */}
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 relative overflow-hidden group">
              {/* Background Pattern */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-pink-50 to-pink-100 rounded-full -translate-y-16 translate-x-16 opacity-60 group-hover:opacity-80 transition-opacity"></div>
              
              {/* Icon */}
              <div className="relative z-10 w-16 h-16 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </div>
              
              {/* Content */}
              <div className="relative z-10">
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-pink-600 transition-colors">
                  Lifetime Community Access
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Join a learner community with peer support and continuous learning updates
                </p>
                <div className="flex items-center text-xs text-pink-600 font-medium">
                  <span className="w-2 h-2 bg-pink-600 rounded-full mr-2"></span>
                  Forever Connected
                </div>
              </div>
            </div>
          </div>


        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4" style={{ backgroundColor: '#F1EFED' }}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Frequently Asked <span className="text-blue-600">Questions</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Everything you need to know about the 10x Data Analyst and AI Complete Certificate Program
            </p>
          </div>

          {/* FAQ Accordion */}
          <div className="max-w-4xl mx-auto">
            <div className="space-y-0">
              {[
                {
                  question: "Who is this program for?",
                  answer: "This program welcomes individuals looking to pivot into exciting data-focused careers or those seeking to deepen their understanding of AI and Data Science, even if they're transitioning from non-tech backgrounds."
                },
                {
                  question: "When do the live sessions happen?",
                  answer: "Live sessions are typically held weekly on weekends, making it easier for working professionals to participate."
                },
                {
                  question: "What's included with the program?",
                  answer: "The program includes comprehensive coursework, live sessions, personalized mentorship, real-world projects, career support, and access to our vibrant alumni network."
                },
                {
                  question: "What skills will I learn in this program?",
                  answer: "You'll gain practical skills in data analysis techniques, SQL, Python, basic machine learning, data visualization, and introductory AI concepts—all essential for today's data-driven landscape."
                },
                {
                  question: "What professional paths can this program support?",
                  answer: "Graduates often find themselves well-equipped for various roles, including Data Analyst, Business Intelligence Analyst, and Junior Data Scientist."
                },
                {
                  question: "When will I receive my Course Certificate, and how long is the program?",
                  answer: "You'll receive your 1to10x Certificate upon successfully completing all coursework and projects, typically within 6 months from the start of the program."
                }
              ].map((faq, index) => (
                <div key={index} className="border-b border-gray-300 last:border-b-0">
                  <details className="group">
                    <summary className="flex justify-between items-center cursor-pointer text-left text-gray-900 hover:text-gray-700 text-lg font-medium py-6 hover:no-underline list-none">
                      <span>{faq.question}</span>
                      <svg 
                        className="w-5 h-5 text-gray-500 transform transition-transform group-open:rotate-180" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </summary>
                    <div className="text-gray-600 pb-6 pt-2 leading-relaxed text-base">
                      {faq.answer}
                    </div>
                  </details>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - Redesigned with Mouse Tracking */}
      <section 
        className="relative py-24 px-4 overflow-hidden"
        style={{ 
          backgroundColor: '#2563EB'
        }}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          
          // Update CSS custom properties for mouse position
          e.currentTarget.style.setProperty('--mouse-x', `${x}px`);
          e.currentTarget.style.setProperty('--mouse-y', `${y}px`);
        }}
      >
        {/* Animated Background Circles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Static decorative circles */}
          <div className="absolute top-10 left-10 w-32 h-32 border-2 border-white/20 rounded-full animate-pulse"></div>
          <div className="absolute top-32 right-20 w-24 h-24 border border-white/15 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-20 left-1/4 w-40 h-40 border-2 border-white/10 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
          <div className="absolute bottom-32 right-10 w-28 h-28 border border-white/20 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
          
          {/* Mouse tracking circles */}
          <div className="mouse-circle-1 absolute w-96 h-96 border border-white/20 rounded-full pointer-events-none"></div>
          <div className="mouse-circle-2 absolute w-64 h-64 border border-white/15 rounded-full pointer-events-none"></div>
          <div className="mouse-circle-3 absolute w-32 h-32 border-2 border-white/25 rounded-full pointer-events-none"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            Ready to <span className="text-blue-200">10X</span> Your Data Career?
          </h2>
          <p className="text-xl md:text-2xl mb-8 text-white/90 max-w-4xl mx-auto">
            Join professionals building practical analytics skills through guided, AI-powered learning
          </p>
          <button 
            onClick={() => openPopup()}
            className="bg-white hover:bg-gray-100 text-blue-600 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl border-2 border-white hover:border-gray-100"
          >
            Book Your Strategy Call Today
          </button>
        </div>

        {/* Custom CSS for mouse tracking animation */}
        <style dangerouslySetInnerHTML={{
          __html: `
            .mouse-circle-1 {
              transform: translate(calc(var(--mouse-x, 50%) - 50%), calc(var(--mouse-y, 50%) - 50%));
              transition: transform 0.3s ease-out;
            }
            .mouse-circle-2 {
              transform: translate(calc(var(--mouse-x, 50%) - 50%), calc(var(--mouse-y, 50%) - 50%));
              transition: transform 0.4s ease-out;
            }
            .mouse-circle-3 {
              transform: translate(calc(var(--mouse-x, 50%) - 50%), calc(var(--mouse-y, 50%) - 50%));
              transition: transform 0.2s ease-out;
            }
          `
        }} />
      </section>

      <Footer />
      
      <ContactPopup
        isOpen={popupState.isOpen}
        onClose={closePopup}
        title={popupState.title}
        subtitle={popupState.subtitle}
        formType={popupState.formType}
        interestedProgram={popupState.interestedProgram}
      />
    </div>
  );
};

export default DataAnalystCourse; 
